import { useState } from 'react';
import { useLocation, Link } from 'wouter';

export function ResetPassword() {
    const [, setLocation] = useLocation();
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    // Extract token from URL
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        setLoading(true);

        try {
            const res = await fetch('/api/auth/reset-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, newPassword, confirmPassword }),
            });
            const data = await res.json();

            if (!res.ok) {
                setError(data.error || 'Reset failed');
                return;
            }

            setSuccess(true);
            setTimeout(() => setLocation('/login'), 3000);
        } catch (err: any) {
            setError(err.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
                <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 shadow-xl text-center">
                    <div className="text-4xl mb-4">⚠️</div>
                    <h1 className="text-xl font-bold text-slate-900 mb-2">Invalid Reset Link</h1>
                    <p className="text-slate-500 text-sm mb-4">This link is missing a reset token. Please request a new one.</p>
                    <Link href="/forgot-password" className="text-primary hover:text-primary/80 text-sm font-medium">Request New Link</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
            <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 shadow-xl">
                <div className="text-center mb-6">
                    <div className="text-4xl mb-4">🔐</div>
                    <h1 className="text-2xl font-bold text-slate-900 mb-2">Reset Password</h1>
                    <p className="text-slate-500 text-sm">Enter your new password below.</p>
                </div>

                {success ? (
                    <div className="text-center space-y-4">
                        <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-4 text-sm">
                            Password reset successfully! Redirecting to login...
                        </div>
                    </div>
                ) : (
                    <>
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-3 text-sm mb-6 text-center">{error}</div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="space-y-1">
                                <label htmlFor="newPassword" className="text-sm font-medium text-slate-700">New Password</label>
                                <input
                                    id="newPassword"
                                    type="password"
                                    className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    minLength={8}
                                    autoFocus
                                />
                            </div>

                            <div className="space-y-1">
                                <label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">Confirm Password</label>
                                <input
                                    id="confirmPassword"
                                    type="password"
                                    className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                />
                                {confirmPassword && newPassword !== confirmPassword && (
                                    <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="h-12 w-full bg-primary hover:bg-primary/90 text-white rounded-lg font-semibold transition disabled:opacity-60"
                                disabled={loading || (confirmPassword !== '' && newPassword !== confirmPassword)}
                            >
                                {loading ? 'Resetting...' : 'Reset Password'}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
